/**
 * ChartCard — the shared shell every financial chart repeated by hand
 * (Card + title + subtitle + optional action slot + optional footnote + empty
 * state). Plus the focal badge and the two tick renderers that were duplicated
 * across ~8 charts.
 *
 * Tick renderers are FACTORIES, not components: Recharts passes the `tick` prop
 * a render function, so `focalTickFactory(name)` returns `(props) => <g/>`.
 */
import type { ReactNode } from 'react';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { CHART_INK, CHART_TYPE, FOCAL_COLOR } from './chartTheme';
import type { DataNote } from '../../../types/financial';

export interface ChartCardProps {
  title: string;
  subtitle?: string;
  /** Right-aligned slot in the header — the focal badge or a chart/table toggle. */
  action?: ReactNode;
  /** Rendered below the chart body — e.g. the ⚠ "adjusted years" line. */
  footnote?: ReactNode;
  /** When set, the body renders this centered message instead of `children`. */
  empty?: string;
  children?: ReactNode;
  className?: string;
}

export default function ChartCard({
  title,
  subtitle,
  action,
  footnote,
  empty,
  children,
  className = '',
}: ChartCardProps) {
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
            {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      </CardHeader>
      <CardBody>
        {empty ? (
          <p className="text-sm text-neutral-500 text-center py-8">{empty}</p>
        ) : (
          children
        )}
        {!empty && footnote}
      </CardBody>
    </Card>
  );
}

/** The ★ Focal pill shown in a chart header in peer-comparison mode. */
export function FocalBadge({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-50 border border-secondary-200 px-2 py-0.5 text-[10px] font-medium text-secondary-800 shrink-0">
      ★ Focal: {name}
    </span>
  );
}

/**
 * Reporting-period pill for the peer-comparison charts. Those show each
 * company's LATEST reported year (not an average), so surfacing the year makes
 * that unambiguous. `label` is a pre-formatted fiscal year (e.g. "FY26").
 */
export function YearBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 border border-neutral-200 px-2 py-0.5 text-[10px] font-medium text-neutral-600 shrink-0">
      Latest: {label}
    </span>
  );
}

/**
 * Rotated category tick that bolds + stars the focal company. Compares
 * case-insensitively against `focalName`.
 */
export function focalTickFactory(focalName: string) {
  const focalLower = focalName.toLowerCase();
  return function FocalTick(props: any) {
    const { x, y, payload } = props;
    const isFocal = String(payload.value).toLowerCase() === focalLower;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          transform="rotate(-25)"
          fontSize={CHART_TYPE.axisTick}
          fontWeight={isFocal ? 700 : 400}
          fill={isFocal ? FOCAL_COLOR : CHART_INK.label}
        >
          {isFocal ? '★ ' : ''}
          {payload.value}
        </text>
      </g>
    );
  };
}

/**
 * Year tick that flags fiscal years whose figures were adjusted/estimated with a
 * ⚠, so a restated year is not silently indistinguishable from an observed one.
 * Pass either a Set of flagged year labels or a Map of year → notes.
 */
export function yearTickFactory(flagged: Set<string> | Map<string, DataNote[]>) {
  const notesFor = (value: string): DataNote[] =>
    flagged instanceof Set ? [] : (flagged.get(value) ?? []);
  const isFlagged = (value: string) =>
    flagged instanceof Set ? flagged.has(value) : (flagged.get(value)?.length ?? 0) > 0;
  return function YearTick(props: any) {
    const { x, y, payload } = props;
    const value = String(payload.value);
    const flag = isFlagged(value);
    // Native SVG tooltip on the tick itself. The Recharts <Tooltip> only fires
    // over the plot area and needs a data point to anchor to, so an empty year
    // (a gap, no bar/point) is unreachable that way — but its ⚠ axis label is
    // always here to hover. This guarantees the "hover the year for the reason"
    // affordance works even where the series has nothing to plot.
    const tip = notesFor(value)
      .map((n) => (n.message ? `${n.title}: ${n.message}` : n.title))
      .join('\n\n');
    return (
      <g transform={`translate(${x},${y})`} style={{ cursor: flag && tip ? 'help' : 'default' }}>
        {flag && tip && <title>{tip}</title>}
        <text
          dy={14}
          textAnchor="middle"
          fontSize={CHART_TYPE.axisTick}
          fill={flag ? CHART_INK.flagged : CHART_INK.label}
        >
          {flag ? '⚠ ' : ''}
          {payload.value}
        </text>
      </g>
    );
  };
}
